// Watchtower types for Supabase integration

export interface WatchtowerPost {
  id: string;
  author_id: string;
  title: string;
  slug?: string | null; // readable URL segment, /watchtower/{slug}
  seo_title?: string | null; // optional search title; falls back to a trimmed title
  content: string;
  excerpt?: string;
  image_url?: string;
  image_urls?: string[]; // optional multi-image support
  image_alt?: string;
  tags: string[];
  is_published: boolean;
  is_featured: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
  published_at: string;
  search_vector?: string;
}

export interface WatchtowerComment {
  id: string;
  post_id: string;
  author_id: string;
  content: string;
  parent_id?: string;
  is_approved: boolean;
  created_at: string;
  updated_at: string;
}

export interface WatchtowerPostLike {
  id: string;
  post_id: string;
  user_id: string;
  created_at: string;
}

export interface WatchtowerCommentLike {
  id: string;
  comment_id: string;
  user_id: string;
  created_at: string;
}

export interface WatchtowerSubscription {
  id: string;
  user_id: string;
  email?: string;
  is_active: boolean;
  created_at: string;
}

// Extended types with author metadata
export interface WatchtowerPostWithAuthor extends WatchtowerPost {
  author_name: string;
  author_avatar_url?: string;
  comment_count: number;
  like_count: number;
}

export interface WatchtowerCommentWithAuthor extends WatchtowerComment {
  author_name: string;
  author_avatar_url?: string;
  like_count: number;
}

// Search result type
export interface WatchtowerSearchResult {
  id: string;
  title: string;
  excerpt?: string;
  content: string;
  image_url?: string;
  tags: string[];
  view_count: number;
  created_at: string;
  published_at: string;
  author_name: string;
  author_avatar_url?: string;
  comment_count: number;
  like_count: number;
  rank: number;
}

// API request/response types
export interface CreatePostRequest {
  title: string;
  seo_title?: string | null;
  content: string;
  excerpt?: string;
  image_url?: string;
  image_urls?: string[];
  image_alt?: string;
  tags?: string[];
  is_published?: boolean;
  is_featured?: boolean;
}

export interface UpdatePostRequest extends Partial<CreatePostRequest> {
  id: string;
}

export interface CreateCommentRequest {
  post_id: string;
  content: string;
  parent_id?: string;
}

export interface UpdateCommentRequest {
  id: string;
  content: string;
}

export interface SearchPostsRequest {
  query: string;
  limit?: number;
  offset?: number;
}

export interface GetPostsRequest {
  limit?: number;
  offset?: number;
  featured_only?: boolean;
  published_only?: boolean;
  tags?: string[];
}

// Database table names
export const WATCHTOWER_TABLES = {
  POSTS: 'watchtower_posts',
  COMMENTS: 'watchtower_comments',
  POST_LIKES: 'watchtower_post_likes',
  COMMENT_LIKES: 'watchtower_comment_likes',
  SUBSCRIPTIONS: 'watchtower_subscriptions',
} as const;
